import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { ChevronLeft, ChevronRight, Leaf, LoaderCircle, Star } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { useOwnerDisbursements } from "@/queries/financials/useOwnerDisbursements";
import { currencyFormat } from "@/util/formats";
import { groupEligibleDisbursementsBySydneyQuarter } from "@/util/sydneyQuarter";
import {
  findBestQuarterSortKey,
  energyBillUpdatedEvent,
  getBarHeight,
  getChartFractionDigits,
  getChartScaleMax,
  parseStoredEnergyBill,
  getVisibleQuarterRange,
} from "./disbursementsChartModel";

const visibleQuarterCount = 8;
const chartPlotHeight = 154;
const defaultEnergyBill = 500;

export default function DisbursementsChartCard() {
  const {
    data: disbursements,
    isPending,
    isError,
    refetch,
  } = useOwnerDisbursements();
  const [billInput, setBillInput] = useState(defaultEnergyBill.toString());
  const [energyBill, setEnergyBill] =
    useState<number | undefined>(defaultEnergyBill);
  const [quarterOffset, setQuarterOffset] = useState(0);

  const quarters = useMemo(
    () => groupEligibleDisbursementsBySydneyQuarter(disbursements ?? []),
    [disbursements]
  );
  const maximumQuarterOffset = Math.max(
    0,
    quarters.length - visibleQuarterCount
  );
  const currentQuarterOffset = Math.min(quarterOffset, maximumQuarterOffset);
  const { start: quarterWindowStart, end: quarterWindowEnd } =
    getVisibleQuarterRange(
      quarters.length,
      visibleQuarterCount,
      currentQuarterOffset
    );
  const visibleQuarters = useMemo(
    () => quarters.slice(quarterWindowStart, quarterWindowEnd),
    [quarterWindowEnd, quarterWindowStart, quarters]
  );
  const canScrollLeft = currentQuarterOffset < maximumQuarterOffset;
  const canScrollRight = currentQuarterOffset > 0;
  const bestQuarterSortKey = useMemo(
    () => findBestQuarterSortKey(quarters),
    [quarters]
  );
  const chartScaleMax = useMemo(
    () => getChartScaleMax(visibleQuarters, energyBill),
    [energyBill, visibleQuarters]
  );

  useEffect(() => {
    setQuarterOffset(0);
  }, [quarters.length]);

  useEffect(() => {
    const storedBill = parseStoredEnergyBill(localStorage.getItem("energy_bill"));
    if (storedBill === undefined) return;

    setBillInput(storedBill.toString());
    setEnergyBill(storedBill > 0 ? storedBill : undefined);
  }, []);

  const updateEnergyBill = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const updatedBill = parseStoredEnergyBill(billInput);
    if (updatedBill === undefined) return;

    localStorage.setItem("energy_bill", updatedBill.toString());
    setEnergyBill(updatedBill > 0 ? updatedBill : undefined);
    window.dispatchEvent(new Event(energyBillUpdatedEvent));
  };

  const scrollChart = (direction: -1 | 1) => {
    setQuarterOffset((offset) =>
      Math.max(0, Math.min(maximumQuarterOffset, offset + direction))
    );
  };

  const hasEnergyBill = energyBill !== undefined && energyBill > 0;
  const formattedBill = (amount: number) =>
    currencyFormat(amount, {
      fractionalDigits: getChartFractionDigits(amount),
      excludeCurrencyId: true,
    }) ?? "";
  const formattedDisbursement = (amount: number) =>
    currencyFormat(amount, {
      fractionalDigits: 0,
      excludeCurrencyId: true,
    }) ?? "";

  return (
    <Card
      aria-busy={isPending}
      className="flex h-full min-h-[330px] w-full flex-col rounded-[12px] border-[#14263A] bg-[#07111D] py-0 text-[#D0D0D0] shadow-none"
    >
      <CardContent className="flex min-h-0 flex-1 flex-col px-4 py-[15px]">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <h2 className="text-[15px] font-bold leading-[18px] tracking-tight text-white">
              Your SolarCloud Disbursements
            </h2>
            <p className="mt-1 text-[11px] leading-[14px] text-[#AAB7C5]">
              Enter your current energy bill per quarter to see how much SolarCloud can save you.
            </p>
          </div>

          <form
            className="flex shrink-0 items-center gap-2"
            onSubmit={updateEnergyBill}
          >
            <label className="flex h-8 w-[124px] items-center gap-1.5 rounded-[8px] border border-[#405064] bg-[#080F19] px-2.5 text-[11px] text-[#D0D0D0]">
              <span className="sr-only">Energy bill per quarter in AUD</span>
              <span aria-hidden="true" className="text-[#D6DEE8]">$</span>
              <input
                aria-label="Energy bill per quarter in AUD"
                className="w-full min-w-0 bg-transparent font-semibold outline-none placeholder:text-[#6B7280]"
                min="0"
                onChange={(event) => setBillInput(event.target.value)}
                required
                step="any"
                type="number"
                value={billInput}
              />
              <span className="text-[9px] text-[#AAB7C5]">AUD</span>
            </label>
            <Button
              className="h-8 shrink-0 rounded-[7px] bg-[#FFB81C] px-3 text-[11px] font-bold text-[#080A0E] transition-colors hover:bg-[#FFC64D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E9A837]"
              type="submit"
            >
              Update
            </Button>
          </form>
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#C8D2DD]">
            <LegendItem colorClassName="bg-[#4AC91B]" label="SolarCloud Disbursements" />
            {hasEnergyBill && (
              <LegendItem colorClassName="bg-[#5D6878]" label="You Paid" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-medium text-[#AAB7C5]">
              All amounts in AUD
            </p>
            <ChartScrollButton
              ariaLabel="Show earlier quarters"
              disabled={!canScrollLeft}
              onClick={() => scrollChart(1)}
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </ChartScrollButton>
            <ChartScrollButton
              ariaLabel="Show later quarters"
              disabled={!canScrollRight}
              onClick={() => scrollChart(-1)}
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </ChartScrollButton>
          </div>
        </div>

        {isPending ? (
          <div
            aria-live="polite"
            className="flex min-h-[160px] flex-1 items-center justify-center gap-2 text-sm text-[#9CA3AF]"
            role="status"
          >
            <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
            Loading disbursement history…
          </div>
        ) : isError && quarters.length === 0 ? (
          <div
            className="flex min-h-[140px] flex-1 flex-col items-center justify-center gap-3 text-center"
            role="alert"
          >
            <p className="text-sm text-[#9CA3AF]">
              Unable to load verified payout history.
            </p>
            <Button
              variant="outline"
              className="rounded-lg border border-[#2F3648] px-3 py-1.5 text-sm font-medium text-[#D0D0D0] hover:bg-[#181C25]"
              onClick={() => void refetch()}
              type="button"
            >
              Retry
            </Button>
          </div>
        ) : quarters.length === 0 ? (
          <div className="flex min-h-[140px] flex-1 items-center justify-center px-4 text-center text-sm text-[#6B7280]">
            No completed payouts are available yet.
          </div>
        ) : (
          <>
            {isError && (
              <p className="mt-3 text-xs text-amber-300" role="status">
                Showing the last loaded payout history; the latest refresh failed.
              </p>
            )}
            <div
              aria-label="Quarterly SolarCloud disbursements"
              className="mt-2 grid min-h-[220px] flex-1 grid-cols-[28px_minmax(0,1fr)] gap-1"
            >
              <div className="flex h-[220px] flex-col justify-between pb-8 pt-2 pr-1 text-right text-[9px] leading-none text-[#C2CDD9]">
                <span>$800</span>
                <span>$600</span>
                <span>$400</span>
                <span>$200</span>
                <span>$0</span>
              </div>
              <div className="grid h-[220px] min-w-0 grid-cols-8 gap-0.5">
                {visibleQuarters.map(({ quarter, amount }) => (
                  <QuarterBar
                    amount={amount}
                    bill={hasEnergyBill ? energyBill : undefined}
                    isBestQuarter={quarter.sortKey === bestQuarterSortKey}
                    key={quarter.sortKey}
                    maxAmount={chartScaleMax}
                    quarterLabel={quarter.label}
                    dateRange={quarter.dateRange}
                    plotHeight={chartPlotHeight}
                    formatBillAmount={formattedBill}
                    formatDisbursementAmount={formattedDisbursement}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LegendItem({
  colorClassName,
  label,
}: {
  colorClassName: string;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className={`size-2.5 rounded-sm ${colorClassName}`} />
      {label}
    </span>
  );
}

function ChartScrollButton({
  ariaLabel,
  children,
  disabled,
  onClick,
}: {
  ariaLabel: string;
  children: ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      aria-label={ariaLabel}
      variant="outline"
      size="icon"
      className="flex size-7 items-center justify-center rounded-full border border-[#405064] bg-[#0B1520] text-[#AAB7C5] transition-colors hover:bg-[#172535] disabled:cursor-default disabled:opacity-55 disabled:hover:bg-[#0B1520]"
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {children}
    </Button>
  );
}

function QuarterBar({
  amount,
  bill,
  isBestQuarter,
  maxAmount,
  quarterLabel,
  dateRange,
  plotHeight,
  formatBillAmount,
  formatDisbursementAmount,
}: {
  amount: number;
  bill?: number;
  isBestQuarter: boolean;
  maxAmount: number;
  quarterLabel: string;
  dateRange: string;
  plotHeight: number;
  formatBillAmount: (amount: number) => string;
  formatDisbursementAmount: (amount: number) => string;
}) {
  const hasBill = bill !== undefined && bill > 0;

  return (
    <div
      className="relative flex min-w-0 flex-col items-center px-0.5 pt-[24px] pb-1"
    >
      {isBestQuarter && (
        <div className="pointer-events-none absolute inset-x-0 top-4 bottom-0 rounded-[8px] border border-[#E9A837] bg-[#E9A837]/[0.03]" />
      )}
      {isBestQuarter && (
        <div className="absolute -top-1.5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-md border border-[#E9A837] bg-[#07111D] px-1.5 py-0.5 text-[7px] font-extrabold tracking-wide text-[#FFB81C]">
          <Star aria-hidden="true" className="size-2.5" />
          <span>BEST QUARTER</span>
        </div>
      )}
      <div
        className="flex w-full items-end justify-center gap-[3px]"
        style={{ height: plotHeight }}
      >
        {hasBill && (
          <BarSegment
            amount={bill}
            colorClassName="bg-gradient-to-b from-[#697584] to-[#3C4552]"
            formatAmount={formatBillAmount}
            maxAmount={maxAmount}
            maxBarHeight={plotHeight}
            widthClassName="w-[38%]"
          />
        )}
        <BarSegment
          amount={amount}
          colorClassName="bg-gradient-to-b from-[#4AC91B] via-[#3DAE11] to-[#267708]"
          formatAmount={formatDisbursementAmount}
          maxAmount={maxAmount}
          maxBarHeight={plotHeight}
          widthClassName={hasBill ? "w-[38%]" : "w-[62%]"}
          showLeaf
        />
      </div>

      <p
        className={`mt-2 w-full truncate text-center text-[11px] font-bold leading-[14px] ${
            isBestQuarter ? "text-[#E9A837]" : "text-[#9CA3AF]"
          }`}
      >
        {quarterLabel}
      </p>
      <p
        className={`w-full truncate text-center text-[10px] leading-[12px] ${
            isBestQuarter ? "text-[#E9A837]/85" : "text-[#6B7280]"
          }`}
      >
        {dateRange}
      </p>
    </div>
  );
}

function BarSegment({
  amount,
  colorClassName,
  formatAmount,
  maxAmount,
  maxBarHeight,
  widthClassName,
  showLeaf = false,
}: {
  amount: number;
  colorClassName: string;
  formatAmount: (amount: number) => string;
  maxAmount: number;
  maxBarHeight: number;
  widthClassName: string;
  showLeaf?: boolean;
}) {
  const height = getBarHeight(amount, maxAmount, maxBarHeight);
  if (height === 0) return <div className={`h-full ${widthClassName}`} />;

  const label = formatAmount(amount);
  const labelOutside = height < (showLeaf ? 36 : 28);

  return (
    <div className={`flex h-full flex-col items-center justify-end ${widthClassName}`}>
      {labelOutside && (
        <span className="mb-1 whitespace-nowrap text-[10px] font-bold text-[#D0D0D0]">
          {label}
        </span>
      )}
      <div
        className={`flex w-full shrink-0 flex-col items-center justify-center overflow-hidden rounded-t-[4px] px-0.5 text-center text-[10px] font-extrabold text-white ${colorClassName}`}
        style={{ height }}
      >
        {!labelOutside && <span className="whitespace-nowrap">{label}</span>}
        {showLeaf && (
          <Leaf
            aria-hidden="true"
            className={`text-white/75 ${height < 36 ? "size-3" : "mt-0.5 size-4"}`}
          />
        )}
      </div>
    </div>
  );
}
